import React, {useContext, useEffect, useState} from "react";
import _ from "lodash";
import Promise from "bluebird";
import ShowProgressSpinner from "../../../common/ui/show-progress-spinner";
import {resetImagePosition} from "../../../shopify/utils/featured-image-util";
import {getAllDimensions} from "../../../master-data/services/dimension-data.service";
import {
    DEFAULT_COLOR_SETUP, DEFAULT_MEDIA_IMAGE_SETTINGS,
    DEFAULT_TEXT_SETTINGS, IMAGE_FIELD_LIST,
    KEY_PREFIX,
    MEDIA_IMAGE_INPUT_FIELDS
} from "../../constants/media.constant";
import {getNextItemKey} from "../../../common/utils/item-key-util";
import {getAllDesignInfo, getSingleDesignInfo} from "../../../shopify/services/design-info-data.service";
import {DESIGN_INFO_IDS, DESIGN_INFO_KEYS} from "../../../shopify/constants/design-info.constant";
import SelectDesignProductImages from "../../../shopify/components/design-info/select-design-product-images";
import ShowSingleCard from "./show-single-card";
import RenderMultipleImageMediaStage from "./render-multiple-image-media-stage";
import {generateLinearGradientDetail} from "../../../common/utils/tailwind-color-util";
import {processBackgroundDetails} from "../../../design/utils/background-util";
import SelectColorTypeDetails from "../../../design/components/design-editor/settings/select-color-type-details";
import {handleFieldValueChanges} from "../../../common/utils/field-change-util";
import ShowSingleCardLayers from "./show-single-card-layers";
import CommonTextSection from "./common-text-section";
import DeleteItem from "../../../common/ui/delete-item";
import {
    addSingleAdImage,
    saveSingleAdImage,
    searchAdImagesWithSearchCriteria,
    updateSingleAdImage
} from "../../services/ad-image-data.service";
import {
    createAdCreative, getAdImagesByHashes, uploadAdImageList,
} from "../../../facebook/services/facebook-ads-service";
import {saveSingleAdCreative} from "../../../facebook/services/facebook-ad-creative-data.service";
import RenderFields from "../../../common/ui/render-fields";
import {DOWNLOAD_IMAGE_TIME_DELAY, LOGO_FIELD_LIST} from "../../../shopify/constants/shopify.constant";
import {processLogoDetails} from "../../../shopify/utils/logo-util";
import {DOWNLOAD_MULTI_IMAGE_TIME_DELAY} from "../../../design/constants/design.constant";
import {getSingleDesignById} from "../../../design/services/design-data-service";
import {
    prepareFacebookAdTextPromptMessages,
    prepareProductDescriptionMetaPromptMessages
} from "../../../design/utils/open-ai-prompt-util";
import {generateJsonContent, generateJsonFormatContent} from "../../../common/services/open-ai-service";

let defaultSettings = DEFAULT_MEDIA_IMAGE_SETTINGS;

const AddEditMultipleImageMedia = ({
                                       isAddImageMedia = false,
                                       isEditImageMedia = false,
                                       isCopyImageMedia = false,
                                       adSet = undefined,
                                       adCreative = undefined,
                                       cssClass = 'button-green px-2 py-1',
                                       afterSaveAction = undefined,
                                   }) => {
    const [showModal, setShowModal] = useState(false);
    const [saveInProgress, setSaveInProgress] = useState(false);
    const [allDesignInfo, setAllDesignInfo] = useState(null);
    const [allDesigns, setAllDesigns] = useState(null);
    const [dimensionsList, setDimensionsList] = useState([]);
    const [defaultDimension, setDefaultDimension] = useState(null);
    const [dimensionsMap, setDimensionsMap] = useState(null);
    const [imageFields, setImageFields] = useState([]);
    const [cards, setCards] = useState([]);
    const [selectedCard, setSelectedCard] = useState(null);
    const [settings, setSettings] = useState(defaultSettings);
    const [designInfo, setDesignInfo] = useState(null);
    const [designDetail, setDesignDetail] = useState(null);
    const [showDesigner, setShowDesigner] = useState(true);
    const [showBackgroundColorSettings, setShowBackgroundColorSettings] = useState(false);
    const [based64DataByCardMap, setBased64DataByCardMap] = useState({});
    const [imageStageMap, setImageStageMap] = useState({});
    const [dataFetchInProgress, setDataFetchInProgress] = useState(false);
    const [adImage, setAdImage] = useState(null);
    const [stageScale, setStageScale] = useState(1);
    const [isSaveActionTriggered, setIsSaveActionTriggered] = useState(false);
    const [adTextContent, setAdTextContent] = useState(null);

    useEffect(() => {
        if (showModal) {
            init();
        }
    }, [showModal]);

    const init = async () => {
        const newDefaultDimension = await prepareMediaImageFields();
        const currentDesignInfo = await getAllDesignsDesignInfo();
        await getDesignDetail();

        if (isEditImageMedia || isCopyImageMedia) {
            const existingAdImage = await getAdImageList();
            defaultSettings = existingAdImage?.settings || defaultSettings;

            if (existingAdImage) {
                prepareExistingAdImage(existingAdImage);
            }
        }

        prepareStageContainerSize(defaultSettings, currentDesignInfo);
    }

    const prepareExistingAdImage = (existingAdImage) => {
        setCards(existingAdImage?.cards);
        setSettings(existingAdImage?.settings);
    }

    const getAdImageList = async () => {
        setDataFetchInProgress(true);

        const searchCriteriaList = [];

        if (adSet) {
            searchCriteriaList.push({field: 'adSetId', condition: '==', value: adSet?.id});
        }

        if (adCreative) {
            searchCriteriaList.push({field: 'adCreativeId', condition: '==', value: adCreative?.id});
        }

        const adImageListResult = await searchAdImagesWithSearchCriteria(searchCriteriaList);
        const existingAdImage = adImageListResult[0] || {};
        setAdImage(existingAdImage);

        setDataFetchInProgress(false);
        return existingAdImage;
    }

    const getAllDesignsDesignInfo = async () => {
        const designInfoResult = await getSingleDesignInfo(adSet?.designId);
        setDesignInfo(designInfoResult);

        if (designInfoResult) {
            await getAllDesignsDesignInfoForFilters(designInfoResult);
        } else {
            const allDesignInfoList = await getAllDesignInfo();
            const allDesignsDesignInfo = _.find(allDesignInfoList, {id: DESIGN_INFO_IDS.ALL_DESIGNS});
            const tempDesignInfoList = _.filter(allDesignInfoList, (designInfo) => designInfo?.id !== DESIGN_INFO_IDS.ALL_DESIGNS);

            setAllDesignInfo(allDesignsDesignInfo);

            const allDesignsResult = _.orderBy(tempDesignInfoList, ['productType', 'category', 'name'], ['asc', 'asc', 'asc']);
            setAllDesigns(allDesignsResult);
        }

        return designInfoResult;
    }

    const getDesignDetail = async () => {
        const designDetailResult = await getSingleDesignById(adSet?.designId);
        setDesignDetail(designDetailResult);
    }

    const getAllDesignsDesignInfoForFilters = async (designInfoResult: any) => {
        const allDesignInfoResult = await getSingleDesignInfo(DESIGN_INFO_IDS.ALL_DESIGNS);

        setAllDesignInfo(allDesignInfoResult);

        if (designInfoResult) {
            setAllDesigns([designInfoResult]);
        }
    }

    const prepareMediaImageFields = async () => {
        const inputFieldsMap = _.keyBy(MEDIA_IMAGE_INPUT_FIELDS, 'attribute');
        let existingDimensions = [...dimensionsList];

        if (_.isEmpty(existingDimensions)) {
            existingDimensions = await getAllDimensions();
            setDimensionsList(existingDimensions);
        }

        inputFieldsMap['dimensionType'].options = existingDimensions?.map((dimension) => {
            return {label: dimension?.name, key: dimension?.id};
        });

        setImageFields(_.values(inputFieldsMap));
        setDimensionsMap(_.keyBy(existingDimensions, 'id'));

        let newDefaultDimension = existingDimensions?.find((dimension) => dimension?.isFacebook);
        // let newDefaultDimension = existingDimensions?.find((dimension) => dimension?.isDefault);
        setDefaultDimension(newDefaultDimension);
        return newDefaultDimension;
    }

    const addNewCard = async (productImage: any, product: any) => {
        const existingCards = [...cards];
        const currentSettings = {...settings};

        const newCard: any = {
            name: 'Card ' + (existingCards?.length + 1),
            key: getNextItemKey(existingCards, KEY_PREFIX.CARD),
            displayOrder: 1,
            url: product?.onlineStoreUrl,
            title: product?.title,
            productHandle: product?.handle,
        };

        newCard.cardName = newCard?.productHandle + '-' + newCard?.key;

        newCard.layers = initDefaultLayers(currentSettings);
        prepareCardBackgroundLayer(newCard, currentSettings);
        const imageLayer = _.find(newCard.layers, {isImageLayer: true});

        handleSelectedProductImageInLayer(productImage, imageLayer, newCard);

        existingCards.push(newCard);
        setCards(existingCards);
    }

    const removeCard = (cardIndex) => {
        let existingCards = [...cards];
        existingCards = existingCards?.filter((cardItem, index) => index !== cardIndex);
        setCards(existingCards);
    }

    const handleSelectedProductImageInLayer = (productImage, imageLayer, currentCard) => {
        const existingSelectedImages = _.filter(imageLayer?.children, (childItem) => childItem?.isImage) || [];

        productImage.layerKey = imageLayer.key;
        productImage.isImage = true;

        const existingProductImage = existingSelectedImages?.find((selectedProductImage) => selectedProductImage?.id === productImage?.id);
        if (!existingProductImage) {
            resetImagePosition(productImage, settings);

            if (!imageLayer?.children) {
                imageLayer.children = [];
            }

            productImage.displayOrder = imageLayer?.children?.length + 1;

            imageLayer.children.push(productImage);
        } else {
            imageLayer.children = imageLayer?.children?.filter((childItem) => childItem?.id !== productImage?.id);
        }

        imageLayer.children = _.sortBy(imageLayer?.children, ['displayOrder']);

        const existingLayers = [...currentCard.layers];
        const currentLayerIndex = _.findIndex(existingLayers, (layer) => layer?.key === imageLayer?.key);
        existingLayers[currentLayerIndex] = imageLayer;
        currentCard.layers = existingLayers;
        setSelectedCard(currentCard);
    }

    const initDefaultLayers = (currentSettings) => {
        const newLayers = [];

        newLayers.push({
            name: 'Layer 1',
            key: getNextItemKey(newLayers, KEY_PREFIX.LAYER),
            displayOrder: 1,
            isBackgroundLayer: true,
        });

        newLayers.push({
            name: 'Layer 2',
            key: getNextItemKey(newLayers, KEY_PREFIX.LAYER),
            displayOrder: 2,
            isImageLayer: true,
        });

        const commonTextLayer = {
            name: 'Layer 3 (Common Texts)',
            key: getNextItemKey(newLayers, KEY_PREFIX.LAYER),
            displayOrder: 3,
            isTextLayer: true,
            isCommonTextLayer: true,
            children: currentSettings?.commonTexts || []
        }

        newLayers.push(commonTextLayer);

        return newLayers;
    }

    const prepareStageContainerSize = (currentSettings, currentDesignInfo, isNew = false) => {
        setShowDesigner(false);

        if (isNew || !(currentSettings?.scale && currentSettings?.stageContainer)) {
            const heightRatio = currentSettings.height / 540;
            const widthRatio = currentSettings.width / 540;

            let sizeRatio = heightRatio < widthRatio ? heightRatio : widthRatio;

            const containerWidth = currentSettings.width / sizeRatio;
            const containerHeight = currentSettings.height / sizeRatio;

            currentSettings.stageContainer = {
                width: _.parseInt(containerWidth?.toFixed(0)),
                height: _.parseInt(containerHeight?.toFixed(0))
            };

            prepareInitialCommonTexts(currentSettings, currentDesignInfo);
            setSettings(currentSettings);
        }

        setTimeout(() => {
            setShowDesigner(true);
        }, 1);
    }

    const prepareInitialCommonTexts = (currentSettings, currentDesignInfo) => {
        const existingCommonTexts = currentSettings?.commonTexts || [];

        existingCommonTexts.push(prepareDesignCommonText(currentDesignInfo?.quoteName, existingCommonTexts));
        existingCommonTexts.push(prepareDesignCommonText(currentDesignInfo?.parent, existingCommonTexts));

        currentSettings.commonTexts = existingCommonTexts;
    }

    const prepareDesignCommonText = (newText, existingCommonTexts) => {
        return {
            isText: true,
            isEditable: false,
            isCommonText: true,
            key: getNextItemKey(existingCommonTexts, KEY_PREFIX.COMMON_TEXT),
            displayOrder: existingCommonTexts?.length + 1,
            text: newText || 'New Text',
            ...DEFAULT_TEXT_SETTINGS
        };
    }

    const handleAttributeChanges = (newAttrs, layerIndex, childIndex, card, stageRef, stageScale) => {
        const existingCards = [...cards];
        const cardIndex = _.findIndex(existingCards, {key: card?.key});

        const existingLayers = card.layers;
        const existingLayer = existingLayers[layerIndex];
        existingLayer.children[childIndex] = newAttrs;
        existingLayers[layerIndex] = existingLayer;
        card.layers = existingLayers;

        existingCards[cardIndex] = card;
        setCards(existingCards);

        /*console.log({stageRef, stageScale});
        handleStageRefChanges(stageRef, stageScale, card?.key);*/
    }

    const handleStageRefChanges = (stageRef, stageScale, imageKey) => {
        /*const existingImageStageMap = {...imageStageMap};
        existingImageStageMap[imageKey] = {
            stageRef: stageRef,
            stageScale: stageScale,
        };

        setImageStageMap(existingImageStageMap);

        console.log({existingImageStageMap});*/

        setImageStageMap({
            ...imageStageMap,
            [imageKey]: {
                stageRef: stageRef,
                stageScale: stageScale,
            }
        });
    }

    const processStageImageBase64Data = () => {
        setSaveInProgress(true);

        const timeoutId =
            setTimeout(async () => {
                let nextIndex = 0;
                let existingCards = [...cards];
                let existingSelectedCard = {...selectedCard};

                if (existingSelectedCard) {
                    const currentIndex = _.findIndex(existingCards, ['key', existingSelectedCard.key]);
                    nextIndex = currentIndex + 1;
                }

                console.log({nextIndex});

                if (existingCards && existingCards?.hasOwnProperty(nextIndex)) {
                    setSelectedCard(existingCards[nextIndex]);
                } else {
                    setSelectedCard(null);
                    // saveImageMediaDetails();
                    const existingImageStageMap = {...imageStageMap};
                    console.log({existingImageStageMap});
                }

            }, 100);

        return () => clearTimeout(timeoutId);
    }

    useEffect(() => {
        const timeoutId =
            setTimeout(async () => {
                if (showModal && isSaveActionTriggered) {
                    let nextIndex = -1;
                    let existingCards = [...cards];
                    let existingSelectedCard = {...selectedCard};

                    if (existingSelectedCard) {
                        const currentIndex = _.findIndex(existingCards, ['key', existingSelectedCard.key]);
                        nextIndex = currentIndex + 1;
                    }

                    if (existingCards && existingCards?.hasOwnProperty(nextIndex)) {
                        setSelectedCard(existingCards[nextIndex]);
                    } else {
                        setIsSaveActionTriggered(false);
                        setSelectedCard(null);
                        saveImageMediaDetails();
                    }
                }

            }, 1000);

        return () => clearTimeout(timeoutId);
    });

    const showOrHideCommonBackgroundColorSettings = () => {
        const currentSettings = {...settings};

        setShowBackgroundColorSettings(!showBackgroundColorSettings);

        const linearGradientDetail = generateLinearGradientDetail();
        currentSettings.background = {
            ...currentSettings.background,
            ...linearGradientDetail
        };

        processBackgroundDetails(currentSettings?.background, currentSettings);
        setSettings(currentSettings);
    }

    const generateRandomCommonBackgroundColor = () => {
        const linearGradientDetail = generateLinearGradientDetail();

        const currentSettings = {...settings};

        currentSettings.background = {
            ...currentSettings.background,
            ...linearGradientDetail
        };

        processBackgroundDetails(currentSettings?.background, currentSettings);
        setSettings(currentSettings);

        prepareAllCardsBackgroundLayer(currentSettings);
    }

    const prepareAllCardsBackgroundLayer = (currentSettings) => {
        const existingCards = [...cards];

        existingCards?.forEach((card) => {
            prepareCardBackgroundLayer(card, currentSettings);
        });

        setCards(existingCards);
    }

    const prepareCardBackgroundLayer = (currentCard, currentSettings) => {
        const existingLayers = [...currentCard?.layers];
        const backgroundLayerIndex = _.findIndex(existingLayers, {isBackgroundLayer: true});
        const backgroundLayer = existingLayers[backgroundLayerIndex];

        if (backgroundLayer) {
            const newBackground = prepareBackgroundLayer(backgroundLayer, currentSettings);

            if (!backgroundLayer?.children) {
                backgroundLayer.children = [];
            }

            const existingBackgroundChildIndex = _.findIndex(backgroundLayer?.children, {isBackground: true});
            if (existingBackgroundChildIndex > -1) {
                backgroundLayer.children[existingBackgroundChildIndex] = newBackground;
            } else {
                backgroundLayer.children.push(newBackground);
            }

            backgroundLayer.children = _.sortBy(backgroundLayer?.children, ['displayOrder']);
            existingLayers[backgroundLayerIndex] = backgroundLayer;
        }

        currentCard.layers = existingLayers;
    }

    const prepareBackgroundLayer = (currentLayer, currentSettings) => {
        const newBackground = {
            isBackground: true,
            layerKey: currentLayer?.key,
            key: getNextItemKey(currentLayer?.children, KEY_PREFIX.BACKGROUND),
            displayOrder: 0,
            settings: {
                height: settings?.height,
                width: settings?.width,
                background: DEFAULT_COLOR_SETUP,
            }
        }

        newBackground.settings.background = currentSettings?.background;
        processBackgroundDetails(newBackground?.settings?.background, newBackground.settings);

        return newBackground;
    }

    const handleCommonSettingsChanges = (event) => {
        const {type, name, value} = event.target;
        let existingSettings = {...settings};
        existingSettings = handleFieldValueChanges(event, existingSettings);

        if (name === 'positionType') {
            processLogoDetails(existingSettings);
        }

        setSettings(existingSettings);

        prepareAllCardsBackgroundLayer(existingSettings);
    }

    const handleCardChanges = async (card) => {
        const existingCards = [...cards];
        const existingCardIndex = _.findIndex(existingCards, {key: card?.key});
        existingCards[existingCardIndex] = card;
        setCards(existingCards);

        if (card?.key === selectedCard?.key) {
            setSelectedCard(card);
        }
    }

    const afterCommonTextAdded = () => {
        const existingCards = [...cards];
        const existingSettings = {...settings};

        _.forEach(existingCards, (card) => {
            const existingLayers = card?.layers;
            const commonTextLayerIndex = _.findIndex(existingLayers, {isCommonTextLayer: true});
            const commonTextLayer = existingLayers[commonTextLayerIndex];

            if (commonTextLayer) {
                commonTextLayer.children = existingSettings?.commonTexts;
            }

            existingLayers[commonTextLayerIndex] = commonTextLayer;
            card.layers = existingLayers;
        });

        setCards(existingCards);
    }

    const saveImageMediaDetails = async () => {
        // setSaveInProgress(true);

        const existingCards = [...cards];
        const existingSettings = {...settings};
        const existingAdImage = {...adImage} || {};
        const existingBase64DataByCardMap = processAllImageBase64Data();
        const existingAdTextContent = {...adTextContent};

        existingAdImage.cards = existingCards;
        existingAdImage.settings = existingSettings;
        existingAdImage.adSetId = adSet?.id;
        existingAdImage.designId = adSet?.designId;
        existingAdImage.campaignId = adSet?.campaign_id;

        const attachments = [];
        const imageHashResponse = await uploadAdImageList(existingBase64DataByCardMap);
        const imageHashMap = imageHashResponse?.images;
        console.log({imageHashMap});

        const imageHashes = [];

        _.forEach(existingCards, (card, cardIndex) => {
            const imageDetail = imageHashMap[card?.cardName + '.png'];
            const adText = existingAdTextContent?.ads[cardIndex] || {};

            if (imageDetail) {
                attachments.push({
                    link: card?.url,
                    // name: card?.title,
                    // name: 'Title',
                    name: adText?.headline || card?.title,
                    description: adText?.link_description || card?.title,
                    // description: card?.title,
                    image_hash: imageDetail.hash
                });

                imageHashes.push(imageDetail.hash);
            }
        });

        console.log({attachments, existingAdTextContent});
        const adLink = _.sample(existingCards)?.url;

        const adCreative: any = {
            name: adSet?.name,
            // name: adSet?.name,
            object_story_spec: {
                link_data: {
                    message: existingAdTextContent?.text,
                    // message: adSet?.name,
                    link: adLink,
                    child_attachments: attachments,
                    call_to_action: {type: "SHOP_NOW", value: {link: adLink}},
                }
            },
            degrees_of_freedom_spec: {
                creative_features_spec: {
                    standard_enhancements: {
                        enroll_status: "OPT_OUT"
                    }
                }
            }
        };

        console.log({existingAdImage, adCreative, adSet});

        const createdAdCreative = await createAdCreative(adCreative);
        console.log({createdAdCreative});

        adCreative.adCreativeId = createdAdCreative?.id;
        adCreative.id = createdAdCreative?.id;
        adCreative.images = _.values(imageHashMap);
        adCreative.adSetId = adSet?.id;
        adCreative.designId = adSet?.designId;
        adCreative.campaignId = adSet?.campaign_id;

        const adImageListResult = await getAdImagesByHashes(imageHashes);
        console.log('adImageListResult', adImageListResult);
        const adImageMap = _.keyBy(adImageListResult?.data, 'hash');

        _.forEach(adCreative?.images, (image) => {
            image.permalink_url = adImageMap[image.hash]?.permalink_url;
        });

        await saveSingleAdCreative(adCreative);

        existingAdImage.adCreativeId = adCreative.adCreativeId;
        existingAdImage.id = adCreative.adCreativeId;
        await saveSingleAdImage(existingAdImage);

        setSaveInProgress(false);

        if (afterSaveAction) {
            afterSaveAction();
        }
    }

    const processAllImageBase64Data = () => {
        const existingBased64DataByCardMap = {...based64DataByCardMap};
        const existingImageStageMap = {...imageStageMap};
        const existingCards = [...cards];

        console.log({existingImageStageMap});

        _.forIn(existingCards, (card, index) => {
            const imageStageDetail = existingImageStageMap[card?.key];
            const pixelRatio = (100 / (imageStageDetail?.stageScale * 100));
            const uri = imageStageDetail?.stageRef?.current?.toDataURL({
                pixelRatio: pixelRatio,
            });

            // const cardName = card?.productHandle + '-' + card?.key;
            existingBased64DataByCardMap[card.cardName] = uri;
        });

        setBased64DataByCardMap(existingBased64DataByCardMap);
        return existingBased64DataByCardMap;
    }

    const generateAdTexts = async () => {
        const existingCards = [...cards];
        setDataFetchInProgress(true);

        const promptMessages = prepareFacebookAdTextPromptMessages(designDetail, existingCards.length);
        const adTextContentResult: any = await generateJsonFormatContent(promptMessages);

        setAdTextContent(adTextContentResult);
        console.log({promptMessages, adTextContentResult});

        /*_.forEach(existingCards, (card, index) => {
            card.adText = adTextContentResult?.ads[index] || {};
        });

        setCards(existingCards);
        console.log({existingCards, adSet, adCreative});*/
        setDataFetchInProgress(false);
    }

    return (
        <>
            {isAddImageMedia &&
                <button
                    className={`${cssClass}`}
                    onClick={() => setShowModal(true)}>
                    Add Image Media
                </button>
            }

            {isEditImageMedia &&
                <button
                    className="button-outline-green px-2 py-1"
                    onClick={() => setShowModal(true)}>
                    Edit Image
                </button>
            }

            {isCopyImageMedia &&
                <button
                    className="button-sky-outline px-2 py-1"
                    onClick={() => setShowModal(true)}>
                    Copy Image
                </button>
            }

            {showModal ? (
                <>
                    <div
                        className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-x-0 top-0 z-50 outline-none focus:outline-none">
                        <div className="relative w-full h-full mx-auto">

                            <div
                                className="border-0 shadow-lg relative flex flex-col w-full bg-gray-100 outline-none focus:outline-none">

                                <div className="flex items-center justify-between p-5 border-b border-solid">

                                    <div className="flex justify-start items-center w-2/3">
                                        <h3 className="text-lg font-semibold w-fit mr-10">
                                            {isAddImageMedia &&
                                                <>Add Image Media</>
                                            }

                                            {isEditImageMedia &&
                                                <>Edit Image Media</>
                                            }

                                            {isEditImageMedia &&
                                                <>Copy Image Media</>
                                            }
                                        </h3>

                                        {/*{imageFields && imageFields.length > 0 && imageMedia &&
                                            <RenderFields
                                                parentForm={imageMedia}
                                                handleDetailChanges={handleImageFieldChanges}
                                                fieldList={imageFields}
                                                enableEdit={true}
                                                fieldClassName={`w-1/2`}/>
                                        }*/}
                                    </div>

                                    <div className="flex items-center justify-end w-1/3">
                                        <div
                                            className="flex items-center justify-end mr-2">
                                            <div className="">
                                                <ShowProgressSpinner
                                                    dataFetchInProgress={saveInProgress || dataFetchInProgress}/>
                                            </div>
                                        </div>

                                        <button
                                            className="button-sky px-3 py-2 mr-2"
                                            onClick={() => {
                                                generateAdTexts();
                                            }}>
                                            Generate Ad Texts
                                        </button>

                                        <button
                                            className="button-green px-3 py-2 mr-2"
                                            onClick={() => {
                                                setSaveInProgress(true);
                                                setIsSaveActionTriggered(true);
                                            }}>
                                            Save Image Media
                                        </button>

                                        {/*<button
                                            className="button-green px-3 py-2 mr-2"
                                            onClick={handleConfirmAction}>
                                            Confirm Image Media
                                        </button>*/}

                                        <button
                                            className="button-gray-outline px-3 py-2"
                                            onClick={() => setShowModal(false)}>
                                            Cancel
                                        </button>
                                    </div>

                                </div>

                                <div className="flex justify-center w-full image-editor-container p-6">

                                    <div className="w-1/3">

                                        <div
                                            className="h-full content-start justify-start overflow-y-scroll mx-2 p-4 border border-gray-400 bg-gray-200 rounded-lg">

                                            <div className={'flex justify-between items-center'}>
                                                <div className={'text-lg font-semibold'}>Image Cards</div>

                                                {/*<button
                                                    className="button-outline-green px-2 py-1 flex justify-center items-center mr-2"
                                                    onClick={() => {
                                                        showOrHideCommonBackgroundColorSettings();
                                                    }}>
                                                    <SwatchIcon className="h-5 w-5"/>
                                                </button>*/}

                                                {allDesignInfo && allDesigns &&
                                                    <SelectDesignProductImages
                                                        allDesignInfo={allDesignInfo}
                                                        allDesigns={allDesigns}
                                                        layer={{}}
                                                        mediaFor={adSet}
                                                        handleSelectedProductImageInLayer={addNewCard}/>
                                                }

                                                {/*<div>
                                                    <button
                                                        className="button-gray-outline pl-2 pr-3 py-1 flex justify-center items-center font-semibold"
                                                        onClick={addNewCard}>
                                                        <PlusIcon className="h-6 w-6 mr-1"/>
                                                        New Card
                                                    </button>
                                                </div>*/}
                                            </div>

                                            <div className={'border border-gray-200 p-4 mt-4 mx-1 rounded bg-gray-100'}>
                                                <div className="flex flex-wrap justify-start">
                                                    <RenderFields
                                                        parentForm={settings}
                                                        handleDetailChanges={handleCommonSettingsChanges}
                                                        fieldList={LOGO_FIELD_LIST}
                                                        enableEdit={true}
                                                        fieldClassName={'w-1/2'}/>
                                                </div>
                                            </div>

                                            <div className={'border border-gray-200 p-4 mt-4 mx-1 rounded bg-gray-100'}>
                                                <div className={'flex justify-between items-center'}>
                                                    <div
                                                        className="text-purple-500 cursor-pointer ml-2 py-2 font-semibold"
                                                        onClick={() => {
                                                            setShowBackgroundColorSettings(!showBackgroundColorSettings);
                                                        }}>
                                                        Background Color & Shade
                                                    </div>

                                                    <button
                                                        className="button-sky-outline px-2 py-1 flex justify-center items-center"
                                                        onClick={() => {
                                                            generateRandomCommonBackgroundColor();
                                                        }}>
                                                        Random Color
                                                    </button>
                                                </div>

                                                <div>
                                                    {showBackgroundColorSettings &&
                                                        <>
                                                            <div className={'mt-2'}>
                                                                <SelectColorTypeDetails
                                                                    hideLabel={true}
                                                                    label={'Background Color & Shade'}
                                                                    parentForm={settings?.background}
                                                                    colorAttribute={'background'}
                                                                    colorKeyAttribute={'backgroundColorKey'}
                                                                    colorCodeKeyAttribute={'backgroundColorCodeKey'}
                                                                    settings={settings}
                                                                    handleColorDetailsChanges={(event) => {
                                                                        handleCommonSettingsChanges(event);
                                                                    }}
                                                                    handleClearColorDetailsChanges={(event) => {
                                                                    }}/>
                                                            </div>
                                                        </>
                                                    }
                                                </div>
                                            </div>

                                            <CommonTextSection
                                                afterCommonTextAdded={afterCommonTextAdded}
                                                settings={settings}
                                                setSettings={setSettings}/>

                                            <div className={'mt-4'}>
                                                {cards?.map((card, cardIndex) => (
                                                    <div key={cardIndex}
                                                         className={`border bg-white rounded m-1 ${selectedCard?.key === card?.key ? 'bg-purple-100 border-purple-400' : 'border-gray-200'}`}>

                                                        <div className={'flex justify-between items-center'}>
                                                            <ShowSingleCard
                                                                card={card}
                                                                selectedCard={selectedCard}
                                                                setSelectedCard={setSelectedCard}
                                                                settings={settings}/>

                                                            <div className={'mr-4'}>
                                                                <DeleteItem
                                                                    handleDeleteItem={() => {
                                                                        removeCard(cardIndex);
                                                                    }}
                                                                    iconSize={'h-4 w-4'}
                                                                    itemIndex={cardIndex}
                                                                    showIcon={true}/>
                                                            </div>
                                                        </div>

                                                        {selectedCard?.key === card?.key &&
                                                            <div className={'px-3 pb-5'}>
                                                                <ShowSingleCardLayers
                                                                    card={card}
                                                                    settings={settings}
                                                                    allDesignInfo={allDesignInfo}
                                                                    allDesigns={allDesigns}
                                                                    afterCardChanges={handleCardChanges}/>
                                                            </div>
                                                        }

                                                    </div>
                                                ))}
                                            </div>

                                        </div>

                                    </div>

                                    <div
                                        className="w-2/3 overflow-scroll bg-gray-200 border border-gray-400 rounded-lg p-4 flex flex-wrap">
                                        {settings && settings?.stageContainer && showDesigner &&
                                            <>
                                                {cards?.map((card, cardIndex) => (
                                                    <RenderMultipleImageMediaStage
                                                        key={cardIndex}
                                                        imageKey={card?.key}
                                                        imageIndex={cardIndex}
                                                        selectedCard={selectedCard}
                                                        imageStageMap={imageStageMap}
                                                        setImageStageMap={setImageStageMap}
                                                        dimensionsMap={dimensionsMap}
                                                        handleAttributeChanges={handleAttributeChanges}
                                                        handleStageRefChanges={handleStageRefChanges}
                                                        parent={card}
                                                        isViewOnly={cardIndex !== 0}
                                                        setSettings={setSettings}
                                                        settings={settings}
                                                        selectedImage={undefined}
                                                        setSelectedImage={undefined}
                                                        setImageBase64={(base64UriData) => {
                                                            // processImageBase64Data(base64UriData, card);
                                                            /*setBased64DataByCardMap({
                                                                ...based64DataByCardMap,
                                                                [card.key]: base64UriData
                                                            });*/
                                                        }}
                                                        imageMedia={undefined}
                                                        stageScale={stageScale}
                                                        setStageScale={setStageScale}/>
                                                ))}
                                            </>
                                        }
                                    </div>

                                </div>

                            </div>
                        </div>
                    </div>

                    <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
                </>
            ) : null}
        </>
    );
}

export default AddEditMultipleImageMedia
